// Content language (decision D37.3): the language in which the models write the text MaestrOS shows to people — task
// titles, descriptions and acceptance criteria from the planner, Maestro findings, builder summaries, council and
// guardian explanations. Code, identifiers, file names, commit messages and technical tokens are never translated.
// The organization sets it (`organizations.content_language`); a project may override it (`projects.content_language`,
// null = inherit). Vocabularies are mirrored by migration 0026 (checks): keep them in step.
import { z } from "zod";

/** BCP-47 language tag as accepted by the database checks (`pt-BR`, `en`, `es`, `zh-Hant-TW`…). */
export const CONTENT_LANGUAGE_PATTERN = /^[a-z]{2,3}(-[A-Za-z0-9]{2,8}){0,3}$/;
export const DEFAULT_CONTENT_LANGUAGE = "en";

/** Languages the Settings selector offers (any valid tag is accepted by the API). */
export const SUGGESTED_CONTENT_LANGUAGES = ["en", "pt-BR", "es", "fr", "de", "it"] as const;

export const contentLanguageSchema = z.string().trim().max(35).regex(CONTENT_LANGUAGE_PATTERN, "a BCP-47 language tag such as pt-BR or en");

/** `GET /v1/organization/settings`. */
export const organizationSettingsSchema = z.object({
  organization_id: z.uuid(),
  content_language: contentLanguageSchema,
});
export type OrganizationSettings = z.infer<typeof organizationSettingsSchema>;

/** `PATCH /v1/organization/settings` (organization admin, audited as `config.changed`). */
export const updateOrganizationSettingsSchema = z.object({ content_language: contentLanguageSchema }).strict();

// ---------- Translate tasks ----------

export const TASK_TRANSLATION_STATES = ["requested", "running", "completed", "failed", "needs_confirmation"] as const;
export type TaskTranslationState = (typeof TASK_TRANSLATION_STATES)[number];

/** Default and ceiling of the cost cap of one translation run (cents). */
export const TASK_TRANSLATION_DEFAULT_CAP_CENTS = 100;
export const TASK_TRANSLATION_MAX_CAP_CENTS = 1000;
/** Tasks translated per run (the rest waits for the next run). */
export const TASK_TRANSLATION_MAX_TASKS = 200;

/** `POST /v1/projects/{id}/task-translations` (maintainer). */
export const taskTranslationRequestSchema = z
  .object({
    /** A paid planner model is used only when this is true (the first request answers `needs_confirmation`). */
    allow_paid: z.boolean().default(false),
    cost_cap_cents: z.number().int().min(1).max(TASK_TRANSLATION_MAX_CAP_CENTS).default(TASK_TRANSLATION_DEFAULT_CAP_CENTS),
  })
  .strict();
export type TaskTranslationRequest = z.infer<typeof taskTranslationRequestSchema>;

export const taskTranslationRunSchema = z.object({
  id: z.uuid(),
  project_id: z.uuid(),
  target_language: z.string(),
  state: z.enum(TASK_TRANSLATION_STATES),
  allow_paid: z.boolean(),
  cost_cap_cents: z.number().int(),
  model: z.string().nullable(),
  paid: z.boolean().nullable(),
  tasks_translated: z.number().int(),
  tasks_skipped: z.number().int(),
  cost_cents: z.number(),
  error: z.string().nullable(),
  created_at: z.iso.datetime(),
  started_at: z.iso.datetime().nullable(),
  completed_at: z.iso.datetime().nullable(),
});
export type TaskTranslationRun = z.infer<typeof taskTranslationRunSchema>;
